/**
 * RichTextEditor — small reusable Tiptap editor surface.
 *
 * Extracted from WikiEditor (which remains the full-page editor with
 * its own header, sidebar, save flow, and tenant-API coupling). This
 * sub-component is a "just the body + toolbar" Tiptap wrapper that any
 * dialog or pane can embed without buying into the WikiEditor's
 * full-screen layout or its tenant-scoped save logic.
 *
 * Library-PR-X1 wires this into the sysadmin Articles dialog so
 * sysadmin authoring uses the same Tiptap experience as tenant — the
 * tenant rule (feedback_ui_parity_sysadmin_tenant.md) requires this.
 *
 * Contract
 * --------
 *   - Caller passes initial HTML in `value`.
 *   - Caller owns save: receives `onChange(html)` on every keystroke
 *     and `onChange(html)` on blur.
 *   - Toolbar is shown unless `toolbar={false}`.
 *   - Placeholder is overridable; defaults to "Start writing...".
 *
 * What this component is NOT
 * --------------------------
 *   - Not coupled to /wiki/articles or any sysadmin endpoint. It does
 *     not call apiClient. Save is the caller's responsibility.
 *   - Not coupled to the EditorHeader / EditorSidebar chrome. Those
 *     belong to the full-page WikiEditor layout.
 *   - Not a Tiptap configuration registry. The extensions list is
 *     duplicated from WikiEditor's editor config so the two surfaces
 *     stay visually identical; that's intentional. A future refactor
 *     can hoist `buildExtensions()` into a shared helper if a third
 *     consumer arrives.
 */

import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TiptapUnderline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import TiptapLink from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TiptapImage from '@tiptap/extension-image';
import { cn } from '@/lib/utils';
import { EditorToolbar } from './EditorToolbar';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Hide the toolbar when false (default: true). */
  toolbar?: boolean;
  /** Minimum editor body height. Default: 320px. */
  minHeightClassName?: string;
  /** Extra class on the outer wrapper. */
  className?: string;
  /** Optional aria-label for the EditorContent. */
  ariaLabel?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start writing...',
  toolbar = true,
  minHeightClassName = 'min-h-[320px]',
  className,
  ariaLabel,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      // Duplicate explicit Link/Underline registrations would trigger
      // Tiptap's "Duplicate extension names" warning; disable the
      // StarterKit copies so our configured versions own the slot.
      StarterKit.configure({
        heading: { levels: [1, 2] },
        codeBlock: {
          HTMLAttributes: {
            class:
              'rounded-md bg-slate-900 text-slate-100 p-4 font-mono text-sm my-4 overflow-x-auto',
          },
        },
        bulletList: { HTMLAttributes: { class: 'list-disc ml-6 space-y-1' } },
        orderedList: { HTMLAttributes: { class: 'list-decimal ml-6 space-y-1' } },
        blockquote: {
          HTMLAttributes: {
            class:
              'border-l-4 border-primary/40 pl-4 py-1 my-4 italic text-muted-foreground bg-muted/20 rounded-r-md',
          },
        },
        link: false,
        underline: false,
      }),
      Placeholder.configure({ placeholder }),
      TiptapLink.configure({ openOnClick: false }),
      TaskList.configure({ HTMLAttributes: { class: 'list-none p-0' } }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: { class: 'flex items-start gap-2 my-1' },
      }),
      TiptapUnderline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TiptapImage.configure({ inline: false }),
    ],
    content: value || '<p></p>',
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none focus:outline-none px-3 py-3',
          '[&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-semibold',
          minHeightClassName,
        ),
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  // If `value` changes externally (e.g. caller resets the form), push
  // the new content into Tiptap. We compare to avoid stomping the
  // cursor during normal typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value || '<p></p>', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  return (
    <div className={cn('rounded-md border bg-background overflow-hidden', className)}>
      {toolbar && (
        <div className="border-b">
          <EditorToolbar editor={editor} />
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
